/**
 * Storage key constants for ProductsRowGroupingDataGrid (Config C).
 * All storage access must use these constants — never inline strings.
 *
 * Two tiers, by design:
 * - PERSISTENT (localStorage): the user's saved/named preset library. Survives
 *   forever, across browser restarts, until the user deletes a preset.
 * - SESSION (sessionStorage): the "currently active" filter/grouping/column
 *   order applied to the grid right now. Resets to the domain default at the
 *   start of each new browser tab/window; survives page refresh and in-app
 *   navigation within that tab.
 *
 * Pattern: forcing a PERSISTENT default onto everyone exactly once
 * ------------------------------------------------------------------
 * A PERSISTENT preference (localStorage) is, by definition, never
 * overwritten by a new code-level default — it was saved specifically so
 * the user's own choice survives every future deploy. That's correct
 * almost always, but occasionally a product decision changes the default
 * so much (e.g. issue #723's column-visibility redesign) that every
 * existing user — including ones who never touched the setting — should
 * see the new default at least once, while still being free to change it
 * again afterward exactly as before.
 *
 * The fix is a second, tiny PERSISTENT key that stores nothing but a
 * version string for the default itself (see
 * STORAGE_KEY_COLUMN_VISIBILITY_DEFAULT_VERSION below for the concrete
 * example, backed by ProductsRowGroupingDataGrid.tsx's
 * COLUMN_VISIBILITY_DEFAULT_VERSION constant). On read:
 *   1. Compare the stored version string to the version constant defined
 *      next to the default value in code.
 *   2. Mismatch (including "never set") → overwrite the real preference
 *      key with the new default, write the version constant to the
 *      version key, and use the new default for this load.
 *   3. Match → skip entirely; read the user's real saved value as normal.
 * Bumping that version constant later re-triggers the override for
 * everyone, once, without any other code change. This is NOT a general
 * schema-version/migration framework — it's a one-off, per-key opt-in;
 * add a matching version key only for a preference that genuinely needs
 * a deliberate, one-time forced reset.
 */

// --- Grouping: SESSION (resets each new tab/window) ---
/** Current grouping dimension order */
export const STORAGE_KEY_GROUPING = 'lgm_products_grouping_model';
/** Active grouping preset key ('NONE' | 'SOURCING' | 'CUSTOM' | `custom_${string}`) */
export const STORAGE_KEY_GROUPING_PRESET = 'lgm_products_active_preset';

// --- Grouping: PERSISTENT (saved preset library) ---
/** User-saved grouping presets (old key — migrated to STORAGE_KEY_GROUPING_PRESETS) */
export const STORAGE_KEY_CUSTOM_PRESETS = 'lgm_products_custom_presets';
/** Canonical user-saved grouping presets key */
export const STORAGE_KEY_GROUPING_PRESETS = 'lgm_grouping_presets';

// --- Filters: PERSISTENT (saved preset library) ---
/** User-saved filter presets */
export const STORAGE_KEY_FILTER_PRESETS = 'lgm_filter_presets';

// --- Filters: SESSION (resets each new tab/window) ---
/**
 * Active live filter state (mode, basic term, advanced query, active preset
 * name) — the "currently applied" filter, distinct from the saved preset
 * library above.
 */
export const STORAGE_KEY_FILTER_ACTIVE = 'lgm_filter_active_preset';
/** Products page column-header filter chips (GridFilterModel) */
export const STORAGE_KEY_GRID_FILTER_MODEL = 'lgm_products_grid_filter_model';

// --- Column visibility & order ---
/** Column visibility model (Record<string, boolean>) — PERSISTENT */
export const STORAGE_KEY_COLUMN_VISIBILITY = 'lgm_products_column_visibility';
/** Column order (string[]) — SESSION (resets each new tab/window) */
export const STORAGE_KEY_COLUMN_ORDER = 'lgm_products_column_order';
/**
 * One-time forced-default marker (string) — PERSISTENT. Concrete example of
 * the "forcing a PERSISTENT default onto everyone exactly once" pattern
 * documented at the top of this file. The version constant
 * (COLUMN_VISIBILITY_DEFAULT_VERSION) lives next to DEFAULT_COLUMN_VISIBILITY
 * in ProductsRowGroupingDataGrid.tsx — bump it there to force another
 * one-time reset for everyone.
 */
export const STORAGE_KEY_COLUMN_VISIBILITY_DEFAULT_VERSION =
  'lgm_products_column_visibility_default_version';

// --- Manufacturers page: SESSION (resets each new tab/window) ---
/** Active live filter state for the Manufacturers page (mirrors STORAGE_KEY_FILTER_ACTIVE) */
export const STORAGE_KEY_MFR_FILTER_ACTIVE = 'lgm_mfr_filter_active';
/** Manufacturers page column-header filter chips (GridFilterModel) */
export const STORAGE_KEY_MFR_GRID_FILTER = 'lgm_mfr_grid_filter';

// --- Manufacturers page: PERSISTENT (saved preset library) ---
export const STORAGE_KEY_MFR_FILTER_PRESETS = 'lgm_mfr_filter_presets';

// --- Grades (Product Types) page: SESSION (resets each new tab/window) ---
export const STORAGE_KEY_GRADES_FILTER_ACTIVE = 'lgm_grades_filter_active';
export const STORAGE_KEY_GRADES_GRID_FILTER = 'lgm_grades_grid_filter';

// --- Grades page: PERSISTENT (saved preset library) ---
export const STORAGE_KEY_GRADES_FILTER_PRESETS = 'lgm_grades_filter_presets';

// --- Therapeutic Areas page: SESSION (resets each new tab/window) ---
export const STORAGE_KEY_TA_FILTER_ACTIVE = 'lgm_ta_filter_active';
export const STORAGE_KEY_TA_GRID_FILTER = 'lgm_ta_grid_filter';

// --- Therapeutic Areas page: PERSISTENT (saved preset library) ---
export const STORAGE_KEY_TA_FILTER_PRESETS = 'lgm_ta_filter_presets';

// --- Audit Logs page: SESSION (resets each new tab/window) ---
export const STORAGE_KEY_AUDIT_FILTER_ACTIVE = 'lgm_audit_filter_active';

// --- Audit Logs page: PERSISTENT (saved preset library) ---
export const STORAGE_KEY_AUDIT_FILTER_PRESETS = 'lgm_audit_filter_presets';

/**
 * Every SESSION-tier key across domains — cleared on a genuinely fresh
 * top-level navigation (see navigation/freshNavigation.ts), which is
 * distinct from a page refresh or in-app back/forward; both of those must
 * leave this state intact per the SESSION-tier contract above.
 */
export const SESSION_STORAGE_KEYS: string[] = [
  STORAGE_KEY_GROUPING,
  STORAGE_KEY_GROUPING_PRESET,
  STORAGE_KEY_FILTER_ACTIVE,
  STORAGE_KEY_GRID_FILTER_MODEL,
  STORAGE_KEY_COLUMN_ORDER,
  STORAGE_KEY_MFR_FILTER_ACTIVE,
  STORAGE_KEY_MFR_GRID_FILTER,
  STORAGE_KEY_GRADES_FILTER_ACTIVE,
  STORAGE_KEY_GRADES_GRID_FILTER,
  STORAGE_KEY_TA_FILTER_ACTIVE,
  STORAGE_KEY_TA_GRID_FILTER,
  STORAGE_KEY_AUDIT_FILTER_ACTIVE,
];
